// paletteTransform.ts
// Business logic for transforming color palettes using LAB color space rotation

import * as THREE from 'three';
import { DynamicColorPalette, Vector3 } from './types';
import { hexToRgb, rgbToXyz, xyzToLab, labToXyz, xyzToRgb, rgbToHex } from './colorUtils';
import { dot, cross, normalize } from './vectorUtils';

/**
 * Transform an entire color palette based on a 3D position on a sphere
 * Uses rotation in LAB color space for perceptually uniform transformations
 */
export const transformPalette = (
  inputColors: DynamicColorPalette,
  position: THREE.Vector3
): DynamicColorPalette => {
  const axis: Vector3 = [position.x, position.y, position.z];
  const reference: Vector3 = [0, 0, 1];
  
  // Calculate rotation parameters
  const crossProduct = cross(reference, axis);
  const dotProduct = Math.min(Math.max(dot(reference, axis), -1), 1);
  const theta = Math.acos(dotProduct);
  const k = normalize(crossProduct);

  const newColors: DynamicColorPalette = {};

  Object.entries(inputColors).forEach(([key, hexColor]) => {
    // Skip rgba colors - they don't transform well
    if (hexColor.includes('rgba')) {
      newColors[key] = hexColor;
      return;
    }

    try {
      // Convert hex -> RGB -> XYZ -> LAB
      const rgb = hexToRgb(hexColor);
      const xyz = rgbToXyz(rgb);
      const lab = xyzToLab(xyz);
      
      // Scale LAB to unit sphere coordinates
      const scaled: Vector3 = [lab[0] / 100, lab[1] / 128, lab[2] / 128];
      
      // Rotate in 3D space
      const rotated = rotateVector(scaled, k, theta);
      
      // Scale back to LAB space
      const labRotated = [rotated[0] * 100, rotated[1] * 128, rotated[2] * 128];
      
      // Convert back: LAB -> XYZ -> RGB -> hex
      const xyzOut = labToXyz(labRotated as [number, number, number]);
      const rgbOut = xyzToRgb(xyzOut);
      const hexOut = rgbToHex(rgbOut);
      
      newColors[key] = hexOut;
    } catch (e) {
      // If conversion fails, keep original color
      newColors[key] = hexColor;
    }
  });

  return newColors;
};

/**
 * Rotate a vector around an axis using Rodrigues' rotation formula
 * Helper function that handles the vector rotation logic
 */
const rotateVector = (v: Vector3, k: Vector3, theta: number): Vector3 => {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const kDotV = dot(k, v);
  const kxv = cross(k, v);
  
  return [
    v[0] * c + kxv[0] * s + k[0] * kDotV * (1 - c),
    v[1] * c + kxv[1] * s + k[1] * kDotV * (1 - c),
    v[2] * c + kxv[2] * s + k[2] * kDotV * (1 - c)
  ];
};